/**
 * Specialised sub-modules — full subsections (13-16) of Dental Health Intelligence.
 *
 * Each module is a richer educational unit than the original FutureModulesPreview
 * cards: scope + key insight + 3-section anatomy of factors (favorable / burden / opportunities)
 * + a mini-score reflecting how well-positioned the user is in that area, derived from
 * the existing DHI snapshot signals (no new backend needed).
 *
 * STRICTLY EDUCATIONAL — never a diagnosis.
 */
import React from "react";
import { Baby, Brackets, Anchor, Microscope, ShieldCheck, AlertTriangle, Lightbulb } from "lucide-react";
import ScoreExplanation from "./ScoreExplanation";

const BAND = { green: "#22C55E", yellow: "#EAB308", orange: "#F97316", red: "#EF4444" };

function bandOf(score) {
  if (score >= 75) return "green";
  if (score >= 55) return "yellow";
  if (score >= 35) return "orange";
  return "red";
}

// ─── Mini-score computations from snapshot.signals ─────────────────

/* Pediatric readiness: protective-nutrition + low sugar + good Vit D. */
function pediatricReadiness(signals) {
  let s = 70;
  if ((signals.sugar_per_day || 0) > 50) s -= 25;
  else if ((signals.sugar_per_day || 0) > 25) s -= 12;
  if ((signals.fiber_per_day || 0) >= 25) s += 10;
  if ((signals.vitamin_d || 0) >= 40) s += 10;
  else if ((signals.vitamin_d || 0) < 20) s -= 8;
  if (signals.smoking) s -= 15;
  return Math.max(0, Math.min(100, s));
}

/* Orthodontic hygiene readiness: low DIL + low snacking + good fiber. */
function orthodonticReadiness(signals) {
  let s = 70;
  if ((signals.dil_avg || 0) > 130) s -= 18;
  if ((signals.meals_last_14d || 0) / 14 > 5) s -= 12;
  if ((signals.fiber_per_day || 0) >= 25) s += 8;
  if (signals.smoking) s -= 10;
  return Math.max(0, Math.min(100, s));
}

/* Implant longevity: no smoking + low HbA1c + good Vit D + low CRP. */
function implantReadiness(signals) {
  let s = 65;
  if (signals.smoking) s -= 30;
  if ((signals.hba1c || 0) >= 6.5) s -= 20;
  else if ((signals.hba1c || 0) >= 5.7) s -= 8;
  if ((signals.hs_crp || 0) > 3) s -= 12;
  if ((signals.vitamin_d || 0) >= 40) s += 12;
  if ((signals.fiber_per_day || 0) >= 25) s += 5;
  return Math.max(0, Math.min(100, s));
}

/* Microbiome balance — re-uses microbiome_intelligence score if available. */
function microbiomeReadiness(snapshot) {
  const mi = snapshot?.microbiome_intelligence?.score;
  if (typeof mi === "number") return mi;
  return 60;
}

// ─── Educational content per module ────────────────────────────────

const CONTENT = {
  bg: {
    pediatric: {
      title: "Педиатрично орално здраве",
      scope: "Малки деца (0-5 г.), деца в училищна възраст (6-12 г.) и тийнейджъри.",
      favorable: [
        "Бутилка само с вода след 12 месеца",
        "Първо посещение при зъболекар до 1-вата година",
        "Smear флуоридна паста до 2 г., грахово зърно 3-6 г.",
        "Балансирана семейна храна (модел Mediterranean)",
      ],
      burden: [
        "Сладки напитки/сокове през деня",
        "Захарни закуски >3×/ден",
        "Бутилка със сладко мляко през нощта",
        "Хронично дишане през устата (ксеростомия)",
      ],
      opportunities: [
        "Xylitol гумички след междинно хранене за >3 г. деца",
        "Седмично проследяване на пробив на постоянни молари (6-7 г.)",
        "Vit D 600 IU/ден за деца 1-18 г. (NIH RDA)",
      ],
      keyInsight: "Превенция в детска възраст е най-силният единичен лост за орално здраве през целия живот.",
      whyInputs: "Дневна захар, фибри, Vit D, тютюнопушене у дома (пасивно).",
      whyFormula: "Започва от 70. Изважда: захар > 50g (−25), > 25g (−12), Vit D < 20 (−8), пасивно пушене (−15). Добавя: фибри ≥ 25g (+10), Vit D ≥ 40 (+10).",
    },
    orthodontic: {
      title: "Ортодонтия (образователно)",
      scope: "Подравняване, оклузия, лечение с брекети/алайнери и ефекти върху денталната хигиена.",
      favorable: [
        "Дневен интердентален четка/флос",
        "Антибактериална вода със CHX 0.05-0.12% при показания",
        "Нощна шина при бруксизъм (по препоръка от ортодонт)",
        "Стрес-регулация — бруксизъмът се влошава от хроничен стрес",
      ],
      burden: [
        "Скъпена хигиена около брекети — белезникави петна (white spot lesions)",
        "Алайнери с натрупана плака от ядене с тях",
        "Бруксизъм + лош сън → ВЧГ напрежение",
        "Кратки месечни прегледи (или липсващи)",
      ],
      opportunities: [
        "Електрическа четка с малка глава за около-брекетна хигиена",
        "Воден флос (Waterpik) — особено при фиксирани брекети",
        "Mindfulness/4-7-8 дишане за стрес-управление",
        "Активно слушане на ортодонта за специфични инструкции",
      ],
      keyInsight: "Ортодонтското лечение умножава ползите от добрата хигиена — и риска от лошата. Не диагностицираме малоклузия.",
      whyInputs: "DIL, честота на хранене, тютюнопушене, фибри.",
      whyFormula: "Започва от 70. Изважда: висок DIL > 130 (−18), > 5 хранения/ден (−12), пушене (−10). Добавя: фибри ≥ 25g (+8).",
    },
    implant: {
      title: "Здраве на импланта",
      scope: "Превенция на пери-имплантит, поддръжка на остеоинтеграция, дългосрочно оцеляване на импланта.",
      favorable: [
        "Непушач — основен фактор за оцеляване на импланта",
        "HbA1c < 6.5% — стабилен метаболизъм за здрави тъкани",
        "Витамин D ≥ 40 ng/ml — здрав костен метаболизъм",
        "Редовни проф. поддръжки (всеки 3-6 месеца)",
      ],
      burden: [
        "Пушенето удвоява риска от пери-имплантит",
        "Неконтролиран диабет (HbA1c ≥ 7%) — забавено заздравяване",
        "Хронично hs-CRP > 3 mg/L — системна инфламация",
        "Пропускане на поддръжките → биофилм нараства неконтролирано",
      ],
      opportunities: [
        "Интердентални четки специално за около-имплантна хигиена",
        "Воден флос Waterpik — премахва плака без да драска повърхността",
        "Омега-3 1-2 g EPA+DHA — антиинфламаторна резолюция",
        "При шевовете на пасивно пушене вкъщи — преговор за общи правила",
      ],
      keyInsight: "Пушенето + диабет — двата най-силни модифицируеми риска. Контролът на едно от двете може да удвои оцеляването на импланта.",
      whyInputs: "Тютюнопушене, HbA1c, hs-CRP, Vit D, фибри.",
      whyFormula: "Започва от 65. Изважда: пушене (−30), HbA1c ≥ 6.5% (−20), 5.7-6.5% (−8), hs-CRP > 3 (−12). Добавя: Vit D ≥ 40 (+12), фибри ≥ 25g (+5).",
    },
    microbiome: {
      title: "Прецизен орален микробиом",
      scope: "Разнообразие на щам-ниво, биофилм карта, бъдеще на персоналното орално здраве.",
      favorable: [
        "S. salivarius K12 — пробиотик с доказана профилактика на халитоза",
        "L. reuteri — намалява гингивално възпаление",
        "Полифеноли (зелен чай, какао) → подкрепят защитната флора",
        "Висок fiber → SCFA продукция → антиинфламаторно",
      ],
      burden: [
        "S. mutans dominance — основен кариоген при висока захар",
        "Lactobacilli — прогресия на кариеса в дентина",
        "P. gingivalis — keystone патоген на пародонтит",
        "T. forsythia + T. denticola — Red Complex (Socransky)",
      ],
      opportunities: [
        "Xylitol 5-10 g/ден — намалява S. mutans",
        "Пробиотични гумки/таблетки с L. reuteri",
        "Намаляване на чести захарни закуски — гладувайте микробите",
        "Когато потребителски тестове станат достъпни — персонална карта",
      ],
      keyInsight: "Орален микробиом — все още нелекуем директно, но мощно модулируем чрез диета, пробиотици и хигиена. Бъдещ модул: персонална микробиомна карта от слюнна проба.",
      whyInputs: "Микробиом баланс от секция 6.",
      whyFormula: "Re-uses Microbiome Intelligence Score (78 базова + защитни/кариогенни/перио-фактори).",
    },
  },
  en: {
    pediatric: {
      title: "Pediatric oral health",
      scope: "Toddlers (0-5y), school-age (6-12y), teens.",
      favorable: [
        "Bottle only with water after 12 months",
        "First dental visit by age 1",
        "Smear fluoride paste ≤2y, rice-grain ≤3y, pea-sized 3-6y",
        "Mediterranean-style family diet",
      ],
      burden: [
        "Sugary drinks/juices throughout the day",
        "Sugary snacks >3×/day",
        "Bottle of sweet milk during sleep",
        "Chronic mouth breathing (xerostomia)",
      ],
      opportunities: [
        "Xylitol gum after meals for kids >3y",
        "Weekly tracking of permanent molar eruption (6-7y)",
        "Vit D 600 IU/d for children 1-18y (NIH RDA)",
      ],
      keyInsight: "Childhood prevention is the strongest single lever for life-long oral health.",
      whyInputs: "Daily sugar, fiber, Vit D, household smoking (passive).",
      whyFormula: "Starts at 70. Subtracts: sugar > 50g (−25), > 25g (−12), Vit D < 20 (−8), passive smoking (−15). Adds: fiber ≥ 25g (+10), Vit D ≥ 40 (+10).",
    },
    orthodontic: {
      title: "Orthodontic (educational)",
      scope: "Alignment, occlusion, treatment with braces/aligners and effects on dental hygiene.",
      favorable: [
        "Daily interdental brush/floss",
        "Antibacterial water with CHX 0.05-0.12% if indicated",
        "Night guard for bruxism (per orthodontist)",
        "Stress regulation — bruxism worsens with chronic stress",
      ],
      burden: [
        "Poor hygiene around braces — white spot lesions",
        "Aligners worn while eating — plaque accumulation",
        "Bruxism + poor sleep → TMJ tension",
        "Missed monthly check-ups",
      ],
      opportunities: [
        "Electric toothbrush with small head for around-bracket hygiene",
        "Water flosser (Waterpik) — especially with fixed braces",
        "Mindfulness/4-7-8 breathing for stress regulation",
        "Active listening to orthodontist's specific instructions",
      ],
      keyInsight: "Orthodontic treatment multiplies the benefits of good hygiene — and the risks of poor hygiene. We don't diagnose malocclusion.",
      whyInputs: "DIL, meal frequency, smoking, fiber.",
      whyFormula: "Starts at 70. Subtracts: high DIL > 130 (−18), > 5 meals/day (−12), smoking (−10). Adds: fiber ≥ 25g (+8).",
    },
    implant: {
      title: "Implant health",
      scope: "Peri-implantitis prevention, osseointegration maintenance, long-term implant survival.",
      favorable: [
        "Non-smoker — primary factor for implant survival",
        "HbA1c < 6.5% — stable metabolism for healthy tissues",
        "Vit D ≥ 40 ng/ml — healthy bone metabolism",
        "Regular maintenance every 3-6 months",
      ],
      burden: [
        "Smoking doubles peri-implantitis risk",
        "Uncontrolled diabetes (HbA1c ≥ 7%) — delayed healing",
        "Chronic hs-CRP > 3 mg/L — systemic inflammation",
        "Skipping maintenance → uncontrolled biofilm growth",
      ],
      opportunities: [
        "Interdental brushes designed for peri-implant hygiene",
        "Water flosser Waterpik — removes plaque without scratching",
        "Omega-3 1-2 g EPA+DHA — pro-resolution",
        "Negotiate household smoke-free rules",
      ],
      keyInsight: "Smoking + diabetes — the two strongest modifiable risks. Controlling either can double implant survival.",
      whyInputs: "Smoking, HbA1c, hs-CRP, Vit D, fiber.",
      whyFormula: "Starts at 65. Subtracts: smoking (−30), HbA1c ≥ 6.5% (−20), 5.7-6.5% (−8), hs-CRP > 3 (−12). Adds: Vit D ≥ 40 (+12), fiber ≥ 25g (+5).",
    },
    microbiome: {
      title: "Precision oral microbiome",
      scope: "Strain-level diversity, biofilm mapping, future of personalised oral health.",
      favorable: [
        "S. salivarius K12 — probiotic with proven halitosis prevention",
        "L. reuteri — reduces gingival inflammation",
        "Polyphenols (green tea, cocoa) → support protective flora",
        "High fiber → SCFA production → anti-inflammatory",
      ],
      burden: [
        "S. mutans dominance — primary cariogen with high sugar",
        "Lactobacilli — caries progression in dentin",
        "P. gingivalis — keystone periodontitis pathogen",
        "T. forsythia + T. denticola — Red Complex (Socransky)",
      ],
      opportunities: [
        "Xylitol 5-10 g/day — reduces S. mutans",
        "Probiotic gum/tablets with L. reuteri",
        "Reduce frequent sugar snacking — starve the microbes",
        "When consumer tests become available — personal map",
      ],
      keyInsight: "Oral microbiome — not yet directly treatable, but powerfully modulable via diet, probiotics, hygiene. Future: personal saliva map.",
      whyInputs: "Microbiome balance from section 6.",
      whyFormula: "Re-uses Microbiome Intelligence Score (78 baseline + protective/cariogenic/perio factors).",
    },
  },
  es: {
    pediatric: {
      title: "Salud bucodental pediátrica",
      scope: "Niños pequeños (0-5 años), edad escolar (6-12 años), adolescentes.",
      favorable: [
        "Biberón solo con agua después de los 12 meses",
        "Primera visita dental antes del año de edad",
        "Pasta fluorada en capa fina ≤2 años, tamaño grano de arroz ≤3 años, tamaño guisante 3-6 años",
        "Dieta familiar de estilo mediterráneo",
      ],
      burden: [
        "Bebidas azucaradas/zumos a lo largo del día",
        "Snacks azucarados >3×/día",
        "Biberón con leche azucarada durante el sueño",
        "Respiración bucal crónica (xerostomía)",
      ],
      opportunities: [
        "Chicle de xilitol después de las comidas para niños >3 años",
        "Seguimiento semanal de la erupción del primer molar permanente (6-7 años)",
        "Vit D 600 UI/día para niños de 1-18 años (NIH RDA)",
      ],
      keyInsight: "La prevención en la infancia es el factor individual más importante para la salud bucodental a lo largo de toda la vida.",
      whyInputs: "Azúcar diario, fibra, Vit D, tabaquismo en el hogar (pasivo).",
      whyFormula: "Comienza en 70. Resta: azúcar > 50 g (−25), > 25 g (−12), Vit D < 20 (−8), tabaquismo pasivo (−15). Suma: fibra ≥ 25 g (+10), Vit D ≥ 40 (+10).",
    },
    orthodontic: {
      title: "Ortodoncia (educativo)",
      scope: "Alineación, oclusión, tratamiento con brackets/alineadores y efectos sobre la higiene dental.",
      favorable: [
        "Cepillo interdental/hilo dental a diario",
        "Agua antibacteriana con CHX 0,05-0,12 % si está indicado",
        "Férula nocturna para el bruxismo (según el ortodoncista)",
        "Regulación del estrés — el bruxismo empeora con el estrés crónico",
      ],
      burden: [
        "Higiene deficiente alrededor de los brackets — lesiones de mancha blanca",
        "Alineadores puestos al comer — acumulación de placa",
        "Bruxismo + sueño deficiente → tensión en la ATM",
        "Revisiones mensuales no realizadas",
      ],
      opportunities: [
        "Cepillo eléctrico con cabezal pequeño para la higiene alrededor de los brackets",
        "Irrigador bucal (Waterpik) — especialmente con brackets fijos",
        "Mindfulness/respiración 4-7-8 para la regulación del estrés",
        "Escucha activa de las instrucciones específicas del ortodoncista",
      ],
      keyInsight: "El tratamiento de ortodoncia multiplica los beneficios de una buena higiene — y los riesgos de una higiene deficiente. No diagnosticamos maloclusión.",
      whyInputs: "DIL, frecuencia de comidas, tabaquismo, fibra.",
      whyFormula: "Comienza en 70. Resta: DIL alto > 130 (−18), > 5 comidas/día (−12), tabaquismo (−10). Suma: fibra ≥ 25 g (+8).",
    },
    implant: {
      title: "Salud del implante",
      scope: "Prevención de periimplantitis, mantenimiento de la osteointegración, supervivencia a largo plazo del implante.",
      favorable: [
        "No fumador — factor principal para la supervivencia del implante",
        "HbA1c < 6,5 % — metabolismo estable para tejidos sanos",
        "Vit D ≥ 40 ng/ml — metabolismo óseo saludable",
        "Mantenimiento regular cada 3-6 meses",
      ],
      burden: [
        "Fumar duplica el riesgo de periimplantitis",
        "Diabetes no controlada (HbA1c ≥ 7 %) — cicatrización retardada",
        "hsCRP crónica > 3 mg/L — inflamación sistémica",
        "Omitir el mantenimiento → crecimiento incontrolado del biofilm",
      ],
      opportunities: [
        "Cepillos interdentales diseñados para la higiene periimplantaria",
        "Irrigador bucal Waterpik — elimina la placa sin rayar",
        "Omega-3 1-2 g EPA+DHA — pro-resolución",
        "Negociar normas de hogar libre de humo",
      ],
      keyInsight: "Tabaquismo + diabetes — los dos factores modificables de mayor riesgo. Controlar cualquiera de ellos puede duplicar la supervivencia del implante.",
      whyInputs: "Tabaquismo, HbA1c, hsCRP, Vit D, fibra.",
      whyFormula: "Comienza en 65. Resta: tabaquismo (−30), HbA1c ≥ 6,5 % (−20), 5,7-6,5 % (−8), hsCRP > 3 (−12). Suma: Vit D ≥ 40 (+12), fibra ≥ 25 g (+5).",
    },
    microbiome: {
      title: "Microbioma oral de precisión",
      scope: "Diversidad a nivel de cepa, mapeo del biofilm, futuro de la salud bucodental personalizada.",
      favorable: [
        "S. salivarius K12 — probiótico con prevención demostrada del mal aliento",
        "L. reuteri — reduce la inflamación gingival",
        "Polifenoles (té verde, cacao) → apoyan la flora protectora",
        "Alta fibra → producción de AGCC → antiinflamatorio",
      ],
      burden: [
        "Dominancia de S. mutans — principal cariógeno con alto consumo de azúcar",
        "Lactobacilos — progresión de caries en dentina",
        "P. gingivalis — patógeno clave de la periodontitis",
        "T. forsythia + T. denticola — Complejo Rojo (Socransky)",
      ],
      opportunities: [
        "Xilitol 5-10 g/día — reduce S. mutans",
        "Chicle/comprimidos probióticos con L. reuteri",
        "Reducir el picoteo frecuente de azúcar — privar de sustrato a los microbios",
        "Cuando las pruebas de consumidor estén disponibles — mapa personal",
      ],
      keyInsight: "Microbioma oral — aún no tratable directamente, pero muy modulable mediante dieta, probióticos e higiene. Futuro: mapa personal de saliva.",
      whyInputs: "Equilibrio del microbioma de la sección 6.",
      whyFormula: "Reutiliza la Puntuación de Inteligencia del Microbioma (base 78 + factores protectores/cariogénicos/periodontales).",
    },
  },
};

const MODULES = [
  { key: "pediatric",   icon: Baby,       color: "#F97316", score: (snap) => pediatricReadiness(snap.signals || {}) },
  { key: "orthodontic", icon: Brackets,   color: "#A855F7", score: (snap) => orthodonticReadiness(snap.signals || {}) },
  { key: "implant",     icon: Anchor,     color: "#0EA5E9", score: (snap) => implantReadiness(snap.signals || {}) },
  { key: "microbiome",  icon: Microscope, color: "#10B981", score: (snap) => microbiomeReadiness(snap) },
];

export default function SpecialisedSubModuleSection({ moduleKey, snapshot, isDark, lang = "bg", cardCls, index }) {
  const bg = lang === "bg";
  const mod = MODULES.find((m) => m.key === moduleKey);
  if (!mod) return null;
  const Icon = mod.icon;
  const c = (bg ? CONTENT.bg : CONTENT.en)[moduleKey];
  const score = mod.score(snapshot || {});
  const band = bandOf(score);
  const color = BAND[band];

  const t = bg
    ? { favorable: "Защитни", burden: "Натоварващи", opportunities: "Възможности", insight: "Ключова мисъл", readiness: "Готовност", soon: "В развитие" }
    : { favorable: "Protective", burden: "Burden", opportunities: "Opportunities", insight: "Key insight", readiness: "Readiness", soon: "Coming soon" };

  return (
    <div className={`${cardCls} p-5 relative overflow-hidden`} data-testid={`dhi-submodule-${moduleKey}`}>
      <div
        className="absolute -top-20 -right-20 w-48 h-48 rounded-full opacity-10 pointer-events-none"
        style={{ background: mod.color }}
      />

      {/* Header */}
      <div className="flex items-start justify-between gap-4 mb-3">
        <div className="flex items-start gap-3">
          <div
            className="w-12 h-12 rounded-xl flex items-center justify-center shrink-0"
            style={{ background: `${mod.color}22`, color: mod.color }}
          >
            <Icon className="w-6 h-6" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h3 className="text-lg font-bold">{c.title}</h3>
              <span className="text-[9px] font-bold px-1.5 py-0.5 rounded uppercase tracking-wider"
                style={{ background: `${mod.color}22`, color: mod.color }}>{t.soon}</span>
            </div>
            <div className="text-[11px] opacity-60 mt-0.5">{c.scope}</div>
          </div>
        </div>
        {/* Mini-score ring */}
        <div className="relative shrink-0" style={{ width: 70, height: 70 }} data-testid={`dhi-submodule-${moduleKey}-score`}>
          <svg width="70" height="70" viewBox="0 0 70 70">
            <circle cx="35" cy="35" r="28" stroke="rgba(0,0,0,0.06)" strokeWidth="6" fill="none" />
            <circle cx="35" cy="35" r="28" stroke={color} strokeWidth="6" strokeLinecap="round" fill="none"
              strokeDasharray={`${(score / 100) * 175.93} 175.93`}
              transform="rotate(-90 35 35)" />
          </svg>
          <div className="absolute inset-0 flex flex-col items-center justify-center">
            <div className="text-lg font-extrabold">{score}</div>
            <div className="text-[8px] opacity-60 uppercase tracking-wider">{t.readiness}</div>
          </div>
        </div>
      </div>

      {/* Three columns: favorable / burden / opportunities */}
      <div className="grid md:grid-cols-3 gap-3 mb-3">
        <Block icon={ShieldCheck} title={t.favorable} items={c.favorable} tone="emerald" isDark={isDark} />
        <Block icon={AlertTriangle} title={t.burden}    items={c.burden}    tone="rose"    isDark={isDark} />
        <Block icon={Lightbulb}     title={t.opportunities} items={c.opportunities} tone="amber" isDark={isDark} />
      </div>

      {/* Key insight */}
      <p className={`text-xs italic leading-snug px-3 py-2 rounded-lg ${isDark ? "bg-white/[0.03]" : "bg-gray-50"}`}
         style={{ borderLeft: `3px solid ${mod.color}` }}>
        <strong className="not-italic">{t.insight}:</strong> {c.keyInsight}
      </p>

      <ScoreExplanation
        isDark={isDark}
        lang={lang}
        testid={`dhi-submodule-${moduleKey}-why`}
        inputs={c.whyInputs}
        formula={c.whyFormula}
      />
    </div>
  );
}

function Block({ icon: Icon, title, items, tone, isDark }) {
  const toneBg = {
    emerald: isDark ? "bg-emerald-500/10 border-emerald-500/20" : "bg-emerald-50 border-emerald-200",
    rose:    isDark ? "bg-rose-500/10 border-rose-500/20"       : "bg-rose-50 border-rose-200",
    amber:   isDark ? "bg-amber-500/10 border-amber-500/20"     : "bg-amber-50 border-amber-200",
  }[tone];
  const iconCls = {
    emerald: "text-emerald-500",
    rose:    "text-rose-500",
    amber:   "text-amber-500",
  }[tone];
  return (
    <div className={`p-3 rounded-lg border ${toneBg}`}>
      <div className={`flex items-center gap-1.5 text-xs font-bold mb-1.5 ${iconCls}`}>
        <Icon className="w-3.5 h-3.5" />
        {title}
      </div>
      <ul className="space-y-1 text-[11px]">
        {items.map((item, i) => (
          <li key={i} className="flex items-start gap-1.5">
            <span className={`mt-1 w-1 h-1 rounded-full shrink-0 ${iconCls.replace("text-", "bg-")}`} />
            <span className="opacity-85 leading-snug">{item}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

// Re-export for tests / external use
export { pediatricReadiness, orthodonticReadiness, implantReadiness, microbiomeReadiness, bandOf };
